import { Component, ElementRef, Inject, Optional, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DomSanitizer } from '@angular/platform-browser';
import * as moment from 'moment';
import { FileUpload } from 'primeng/fileupload';
import { Observable, Subject, Subscription } from 'rxjs';
import { finalize, flatMap, map, takeUntil } from 'rxjs/operators';
import { DocumentSignatureStatusType } from '../../../Foundation/models/enums/document-signature-status-type.enum';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { DocumentData } from '../../models/document/document-data.model';
import { DocumentItemKeyData } from '../../models/document/document-itemkey.model';
import { DocumentRequirementModel } from '../../models/document/document-requirement.model';
import { DocumentSearchCriteria } from '../../models/document/document-search-criteria.model';
import { DocumentTypeDocumentCategoryTypeMapModel } from '../../models/document/document-type-document-category-type-map.model';
import { DocumentViewModel } from '../../models/document/document-view.model';
import { DocumentModel } from '../../models/document/document.model';
import { RequiredDocumentDetailsModel } from '../../models/document/required-document-details.model';
import { DocumentService } from '../../services/document/document.service';
import { AppSettings } from '../../settings/app-settings.model';

export class DocumentUploadData {
    constructor(
        public searchDocumentDataCollection,
        public documentItemKeyCollection,
        public requiredDocumentDetails,
        public isDocumentUploadEnabled: boolean = true,
        public isDocumentNameEditable: boolean = false,
        public isRemoveDocumentEnabled: boolean = true,
        public isDocumentRequirementEnabled: boolean = false,
        public isAddRequirementEnabled: boolean = true,
        public initialDocumentsReviewStatusTypeId: number = null,
        public initialDocuments: File[] = [],
        public defaultSignatureRequired: boolean = false,
        public saveDocumentHandler: (d: DocumentViewModel) => Observable<void> = null
    ) {
    }
}

@Component({
    selector: 'document-upload-modal',
    templateUrl: './document-upload-modal.html'
})
export class DocumentUploadModalComponent extends BaseComponent {
    @ViewChild('fileInput')
    public fileInput: FileUpload;
    @ViewChild('docs')
    public scannedDocuments: ElementRef;

    public form: UntypedFormGroup;
    public fileUploadPath: string;
    public uploadError: boolean;
    public documentCategoryTypes: any[] = [];
    public documentTypes: any = {};
    public documentPrivacyLevels: any[] = [];
    public documentReviewStatusTypes: any[] = [];
    public requiredDocumentDetails: RequiredDocumentDetailsModel[] = [];
    public isBusy: boolean;

    public get documents(): UntypedFormArray {
        return this.form.get('documents') as UntypedFormArray;
    }

    private allDocumentTypes: any[] = [];
    private documentTypeDocumentCategoryTypeMaps: DocumentTypeDocumentCategoryTypeMapModel[] = [];
    private defaultDocumentReviewStatusTypeId = 2; // Accepted
    private defaultDocumentRequirementTypeId = 2; // CSRRequired
    private documentItemKeyCollection: DocumentItemKeyData[];
    private searchDocumentDataCollection: DocumentSearchCriteria[];
    private subscriptions = new Subscription();

    /**
     * Constructs the modal dialog
     * @param public dialog The dialog reference and dialog data
     * @param viewContainerRef
     * @param formBuilder
     * @param imageService
     */
    constructor(
        public dialogRef: MatDialogRef<DocumentUploadModalComponent>,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: DocumentUploadData,
        private documentService: DocumentService,
        private formBuilder: UntypedFormBuilder,
        private settings: AppSettings,
        private sanitizer: DomSanitizer
    ) {
        super();
    }

    public ngOnInit(): void {
        this.form = this.formBuilder.group({
            documents: this.formBuilder.array([])
        });

        if (this.data.initialDocumentsReviewStatusTypeId) {
            this.defaultDocumentReviewStatusTypeId = this.data.initialDocumentsReviewStatusTypeId;
        }

        this.getDocumentTypeDocumentCategoryTypeMaps()
            .pipe(
                flatMap(_ => this.getDocumentLookups()),
                flatMap(_ => this.setDocumentPrivacyLevels()))
            .subscribe(
                _ => this.fileUploadPath = this.settings.api.documentUpload,
                err => this.handleValidationResult(err));
    }

    private getDocumentLookups = (): Observable<any> => this.documentService.getDocumentLookups()
        .pipe(map(res => {
            this.setDocumentLookupDropDowns(res.result);

            return res;
        }));

    private getDocumentTypeDocumentCategoryTypeMaps = (): Observable<any> => this.documentService.getDocumentTypeDocumentCategoryTypeMaps()
        .pipe(map(res => {
            this.documentTypeDocumentCategoryTypeMaps = res.result;

            return res;
        }));

    private setDocumentPrivacyLevels = (): Observable<any> => this.documentService.getDocumentAccessControlsByCategory(1)
        .pipe(map(res => {
            for (var i = 0; i < res.result.length; i++) {
                this.documentPrivacyLevels.push({ label: res.result[i].accessControlTypeDisplayValue, value: res.result[i].accessControlTypeId});
            }

            return res;
        }));

    private setDocumentLookupDropDowns(documentLookups: any): void {
        documentLookups.documentTypes.sort(this.documentTextSort);

        for (var i = 0; i < documentLookups.documentTypes.length; i++) {
            this.allDocumentTypes.push({ label: documentLookups.documentTypes[i].text, value: +documentLookups.documentTypes[i].value });
        }

        documentLookups.documentCategoryTypes.sort(this.documentTextSort);

        for (var i = 0; i < documentLookups.documentCategoryTypes.length; i++) {
            let documentCategoryTypeId = +documentLookups.documentCategoryTypes[i].value;

            if (true == this.documentTypeDocumentCategoryTypeMaps.some(e => (e.documentCategoryTypeId == documentCategoryTypeId) &&
                                                                            (true == this.allDocumentTypes.some(f => f.value == e.documentTypeId)))) {
                this.documentCategoryTypes.push({ label: documentLookups.documentCategoryTypes[i].text, value: documentCategoryTypeId });
            }
        }

        for (var i = 0; i < documentLookups.documentReviewStatusTypes.length; i++) {
            this.documentReviewStatusTypes.push({ label: documentLookups.documentReviewStatusTypes[i].text, value: +documentLookups.documentReviewStatusTypes[i].value });
        }

        this.loadContextData();

        // load initial documents after lookups
        if (this.data.initialDocuments && this.data.initialDocuments.length) {
            this.onFileSelected({ files: this.data.initialDocuments });
        }
    }

    private documentTextSort = (a: any, b: any): number => {
        if (a.text < b.text) {
            return -1;
        } else if (a.text > b.text) {
            return 1;
        }

        return 0;
    };

    private loadContextData(): void {
        this.documentItemKeyCollection = this.data.documentItemKeyCollection;
        this.searchDocumentDataCollection = this.data.searchDocumentDataCollection;

        if (this.data.requiredDocumentDetails && this.data.requiredDocumentDetails.length > 0) {
            for (let i = 0; i < this.data.requiredDocumentDetails.length; i++) {
                let requiredDocumentDetails = new RequiredDocumentDetailsModel();
                requiredDocumentDetails.documentRequirementTypeId = this.data.requiredDocumentDetails[i].documentRequirementTypeId;

                if (this.data.requiredDocumentDetails[i].documentTypeId && this.data.requiredDocumentDetails[i].documentTypeId > 0) {
                    requiredDocumentDetails.documentTypeId = this.data.requiredDocumentDetails[i].documentTypeId;
                    requiredDocumentDetails.documentTypeDisplayValue = this.allDocumentTypes.find(d => d.value == requiredDocumentDetails.documentTypeId).label;
                } else {
                    requiredDocumentDetails.documentTypeDisplayValue = 'Any';
                }

                if (this.data.requiredDocumentDetails[i].documentCategoryTypeId && this.data.requiredDocumentDetails[i].documentCategoryTypeId > 0) {
                    requiredDocumentDetails.documentCategoryTypeId = this.data.requiredDocumentDetails[i].documentCategoryTypeId;
                    requiredDocumentDetails.documentCategoryTypeDisplayValue = this.documentCategoryTypes.find(d => d.value == requiredDocumentDetails.documentCategoryTypeId).label;
                } else {
                    requiredDocumentDetails.documentCategoryTypeDisplayValue = 'Any';
                }

                this.requiredDocumentDetails.push(requiredDocumentDetails);
            }
            // To simplify validation lets sort the required document requirements by those that have both a documentCategoryType and a documentType
            this.requiredDocumentDetails = this.requiredDocumentDetails.sort(function(a, b) {
                return (a.documentCategoryTypeId < b.documentCategoryTypeId && a.documentTypeId < b.documentTypeId) ? 1 :
                    ((a.documentCategoryTypeId < b.documentCategoryTypeId) ? 1 : ((a.documentTypeId < b.documentTypeId) ? 1 : 0));
            });
        }
    }

    public onFileSelected(event: any): void {
        this.clearValidation();

        if (event != null && event.files != null) {
            for (let i = 0; i < event.files.length; i++) {
                let newFile = event.files[i];

                if (!!newFile && ['.pdf', '.png', '.jpeg', '.jpg', '.docx', '.xlsx', '.zip'].some(x => newFile.name.toLowerCase().endsWith(x))) {
                    let document = new DocumentModel();
                    document.documentFileName = newFile.name;
                    document.documentReviewStatusTypeId = this.defaultDocumentReviewStatusTypeId;
                    document.documentSize = newFile.size;
                    document.signatureRequired = this.data.defaultSignatureRequired;
                    this.setDocumentContent(document, newFile)
                        .subscribe(d => this.addDocument(d));
                }
            }
        }
    }

    public documentsInCategory(categoryTypeId: number): DocumentType[] {
        const maps = this.documentTypeDocumentCategoryTypeMaps.filter(e => e.documentCategoryTypeId == categoryTypeId);
        const filteredDocumentTypes = this.allDocumentTypes.filter(e => maps.some(i => i.documentTypeId == e.value));
        return filteredDocumentTypes;
    }

    public scanDocuments(): void {
        if (window['scannerjs']) {
            window['scannerjs'].scan((successful, mesg, response) => {
                if (!successful) { // On error
                    console.error('Failed: ' + mesg);
                    return;
                }

                response = JSON.parse(response);

                if (response != null) {
                    let document = new DocumentModel();
                    document.isScan = true;
                    document.mimeType = 'application/pdf';
                    document.documentFileName = `Scanned_Document_${moment().format()}.pdf`;
                    document.documentReviewStatusTypeId = this.defaultDocumentReviewStatusTypeId;
                    document.documentSize = 0;
                    document.documentContent = response.output[0].result[0];
                    this.addDocument(document);
                }
            }, {
                twain_cap_setting: {
                    CAP_DUPLEXENABLED: '1',
                    ICAP_PIXELTYPE: 'TWPT_GRAY',
                    ICAP_XRESOLUTION: '150',
                    ICAP_YRESOLUTION: '150',
                    ICAP_FRAMES: '(30, 30, 50, 50)'
                },
                output_settings: [{
                    type: 'return-base64',
                    format: 'pdf'
                }],
                recognize_barcodes: true, /** Default value: false */
                barcodes_settings: [{
                    type: 'default',
                    areas: this.getBarcodeAreas()
                }]
            });
        } else {
            console.log('scanner-js not loaded');
        }
    }

    private getBarcodeAreas(): number[][] {
        return [
            [0, 0, 1, 1] // Scanner.js support multiple areas. This demo uses only one.
        ];
    }

    private addDocument(model: DocumentModel): void {
        let formGroup = this.formBuilder.group({
            isScan: [model.isScan],
            mimeType: [model.mimeType],
            documentFileName: [model.documentFileName],
            documentDescription: [model.documentDescription],
            documentReviewStatusTypeId: [model.documentReviewStatusTypeId],
            documentSize: [model.documentSize],
            documentContent: [model.documentContent],
            signatureRequired: [{ value: model.signatureRequired, disabled: this.data.defaultSignatureRequired}],
            signatureComment: [model.signatureComment],
            documentRequirements: this.formBuilder.array([])
        });
        this.documents.push(formGroup);
        this.addDocumentRequirement(formGroup);

        const index = this.documents.length - 1;
        if (this.data.initialDocuments && this.data.initialDocuments.length) {
            this.subscriptions.add(formGroup.get('documentFileName').valueChanges.subscribe(fileName => {
                const blob = this.data.initialDocuments[index].slice(0, this.data.initialDocuments[index].size, this.data.initialDocuments[index].type);
                this.data.initialDocuments[index] = new File([blob], fileName);
            }));
        }
    }

    public addDocumentRequirement(documentForm: UntypedFormGroup): void {
        let requirementGroup = this.formBuilder.group({
            documentTypeId: [null, Validators.required],
            documentCategoryTypeId: [null, Validators.required],
            documentAccessControlTypeId: [null]
        });

        requirementGroup.get('documentCategoryTypeId').valueChanges
            .pipe(takeUntil(this.componentDestroyed))
            .subscribe({
                next: categoryTypeId => {
                    if (categoryTypeId && !(categoryTypeId in this.documentTypes)) {
                        this.documentTypes[categoryTypeId] = this.documentsInCategory(categoryTypeId);
                    }
                }
            });
        let requirements = documentForm.get('documentRequirements') as UntypedFormArray;
        requirements.push(requirementGroup);

        // if a single requiredDocument is passed in then let's default the first uploaded document's category and type to the passed in values
        if (this.documents && this.documents.value.length == 1
            && this.requiredDocumentDetails && this.requiredDocumentDetails.length == 1
            && this.documents.value[0].documentRequirements && this.documents.value[0].documentRequirements.length == 1) {

            if (this.requiredDocumentDetails[0].documentCategoryTypeId > 0) {
                requirementGroup.get('documentCategoryTypeId').setValue(this.requiredDocumentDetails[0].documentCategoryTypeId);
                if (!this.data.isDocumentRequirementEnabled) {
                    requirementGroup.get('documentCategoryTypeId').disable();
                }

                if (this.requiredDocumentDetails[0].documentTypeId > 0) {
                    requirementGroup.get('documentTypeId').setValue(this.requiredDocumentDetails[0].documentTypeId);
                    if (!this.data.isDocumentRequirementEnabled) {
                        requirementGroup.get('documentTypeId').disable();
                    }
                }
            }
        }
    }

    private removeAllDocuments(): void {
        this.documents.clear();
    }

    public removeDocument(index: number): void {
        this.documents.removeAt(index);
    }

    public removeDocumentRequirement(documentFormGroup: UntypedFormGroup, index: number): void {
        let documentRequirements = documentFormGroup.get('documentRequirements') as UntypedFormArray;
        documentRequirements.removeAt(index);
    }

    public upload(): void {
        if (this.form.valid) {
            let documentViewModel = new DocumentViewModel();
            documentViewModel.documents = new Array<DocumentData>();

            for (let i = 0; i < this.documents.length; i++) {
                let documentData = this.getDocumentData(this.documents.at(i) as UntypedFormGroup);
                documentViewModel.documents.push(documentData);
            }

            this.saveDocument(documentViewModel);
        }
    }

    private setDocumentContent(document: DocumentModel, file: File): Observable<DocumentModel> {
        let subject = new Subject<DocumentModel>();
        var reader = new FileReader();

        reader.onload = (doc) => {
            // TODO: RH - verify that read.result is a string
            let content = reader.result.toString();
            let index = content.indexOf(',');
            let contentSubstring = content.substring(index + 1);
            document.documentContent = contentSubstring;
            subject.next(document);
        };

        reader.onloadend = (file) => {
            subject.complete();
        };

        reader.readAsDataURL(file);

        return subject;
    }

    private getDocumentData(formGroup: UntypedFormGroup): DocumentData {
        let documentData = new DocumentData();
        documentData.documentContent = formGroup.get('documentContent').value;
        documentData.documentItemKeys = this.getDocumentItemKeys();
        documentData.documentRequirements = this.getDocumentRequirements(formGroup);
        documentData.documentFilename = formGroup.get('documentFileName').value;
        documentData.documentReviewStatusTypeId = formGroup.get('documentReviewStatusTypeId').value;
        documentData.documentSignatureNotificationComment = formGroup.get('signatureComment').value;
        documentData.documentDescription = formGroup.get('documentDescription').value;
        documentData.documentSignatureStatusTypeId = formGroup.get('signatureRequired').value ? (new DocumentSignatureStatusType).RequiresSignature.id : (new DocumentSignatureStatusType).None.id;

        if (!formGroup.get('isScan').value) {
            let documentFileName = formGroup.get('documentFileName').value;
            let documentSize = formGroup.get('documentSize').value;
            // match by size only
            let file = (this.data.initialDocuments && !this.data.initialDocuments.length) && this.data.isDocumentUploadEnabled
                ? this.fileInput.files.find(f => f.size == documentSize)
                : this.data.initialDocuments.find(f => f.size == documentSize);

            documentData.objectURI = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file));
            documentData.documentFilename = documentFileName || file.name; // prefer form value, fallback to file
            documentData.documentMimeTypeValue = file.type || 'application/octet-stream';
        } else {
            let mimeType = formGroup.get('mimeType').value;
            documentData.documentMimeTypeValue = mimeType || 'application/octet-stream';
        }

        return documentData;
    }

    private getDocumentRequirements(formGroup: UntypedFormGroup): DocumentRequirementModel[] {
        let documentRequirements = new Array<DocumentRequirementModel>();
        let formArray = formGroup.get('documentRequirements') as UntypedFormArray;

        for (let i = 0; i < formArray.length; i++) {
            let requirementForm = formArray.at(i) as UntypedFormGroup;
            let documentRequirement = new DocumentRequirementModel();
            documentRequirement.documentCategoryTypeId = requirementForm.get('documentCategoryTypeId').value;
            documentRequirement.documentTypeId = requirementForm.get('documentTypeId').value;
            documentRequirement.documentRequirementTypeId = this.defaultDocumentRequirementTypeId;
            documentRequirement.documentAccessControlTypeId = requirementForm.get('documentAccessControlTypeId').value;
            documentRequirements.push(documentRequirement);
        }

        return documentRequirements;
    }

    /**
     * Set the passed in documentItemKeys by first checking the documentItemKeyCollection and then the
     * searchDocumentDataCollection
     */
    private getDocumentItemKeys(): DocumentItemKeyData[] {
        let documentItemKeys = new Array<DocumentItemKeyData>();

        if (this.documentItemKeyCollection && this.documentItemKeyCollection.length > 0) {
            for (let i = 0; i < this.documentItemKeyCollection.length; i++) {
                let documentItemKeyData = new DocumentItemKeyData();
                documentItemKeyData.documentItemKeyTypeId = this.documentItemKeyCollection[i].documentItemKeyTypeId;
                documentItemKeyData.itemKeyId = this.documentItemKeyCollection[i].itemKeyId;
                documentItemKeys.push(documentItemKeyData);
            }
        } else if (this.searchDocumentDataCollection && this.searchDocumentDataCollection.length > 0) {
            this.documentItemKeyCollection = new Array<DocumentItemKeyData>();
            for (var i = 0; i < this.searchDocumentDataCollection.length; i++) {
                if (this.searchDocumentDataCollection[i].itemKeyId && this.searchDocumentDataCollection[i].documentItemKeyTypeId) {
                    var itemKeyData = new DocumentItemKeyData();
                    itemKeyData.documentItemKeyTypeId = this.searchDocumentDataCollection[i].documentItemKeyTypeId;
                    itemKeyData.itemKeyId = this.searchDocumentDataCollection[i].itemKeyId;
                    documentItemKeys.push(itemKeyData);
                }
            }
        }

        return documentItemKeys;
    }

    private saveDocument(documentViewModel: DocumentViewModel): void {
        this.isBusy = true;

        if (this.data.saveDocumentHandler) {
            try {
                this.data.saveDocumentHandler(documentViewModel).subscribe(
                    _ => {
                        this.removeAllDocuments();
                        this.dialogRef.close();
                    },
                    error => {
                        this.isBusy = false;
                    });
            } catch {
                this.isBusy = false;
            }
        } else {
            this.documentService.saveDocument(documentViewModel)
                .pipe(finalize(() => this.isBusy = false))
                .subscribe(res => {
                    let result = res;
                    this.removeAllDocuments();
                    this.dialogRef.close(result);
                },
                err => {
                    this.handleValidationResult(err);
                });
        }
    }

    public requirementRequired(formGroup: UntypedFormGroup): boolean {
        return formGroup.get('documentTypeId').disabled;
    }

    /**
     * Closes the modal dialog with no return result
     */
    public close(): void {
        this.dialogRef.close(null);
    }
}
